import React from 'react';
import { Heart, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useLang } from '../i18n';
import { PAYPAL_DONATE_URL } from '../supportData';

// Compact homepage banner inviting voluntary PayPal donations. Mirrors the
// visual language of the book promo banner (rounded card + subtle accent) and
// sits directly above it. Links to the full /supporter page for details.
export const SupporterBanner = ({ className = '' }) => {
  const { lang } = useLang();
  const isDe = lang !== 'en';

  return (
    <section
      className={className}
      aria-label={isDe ? 'Barcode Studio unterstützen' : 'Support Barcode Studio'}
      data-testid="supporter-banner-top"
    >
      <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 blur-2xl"
        />
        <div className="relative flex flex-col items-center gap-4 p-4 sm:flex-row sm:gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Heart className="h-6 w-6" />
          </span>

          <div className="min-w-0 flex-1 text-center sm:text-left">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
              <Heart className="h-3 w-3" />
              {isDe ? 'Unterstützen' : 'Support'}
            </span>
            <h3 className="mt-2 font-heading text-base font-semibold text-foreground sm:text-lg">
              {isDe ? 'Barcode Studio kostenlos halten' : 'Keep Barcode Studio free'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {isDe
                ? 'Kein Premium, keine Bezahlschranke – mit einer freiwilligen Spende hältst du das Tool am Laufen.'
                : 'No Premium, no paywall — a voluntary donation keeps the tool running.'}
            </p>

            <div className="mt-3 flex flex-col items-stretch justify-center gap-2.5 sm:flex-row sm:items-center sm:justify-start">
              <Button asChild size="sm" className="gap-2" data-testid="supporter-banner-donate-btn">
                <a href={PAYPAL_DONATE_URL} target="_blank" rel="noopener noreferrer">
                  <Heart className="h-4 w-4" />
                  {isDe ? 'Mit PayPal spenden' : 'Donate with PayPal'}
                </a>
              </Button>
              <Button asChild variant="outline" size="sm" className="gap-2" data-testid="supporter-banner-more-btn">
                <a href="/supporter">
                  {isDe ? 'Mehr erfahren' : 'Learn more'}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SupporterBanner;
